/**
 * The «Parametri in vigore» sheet every Compacting workbook carries — bata-reqs ADR 9001 decision 5
 * ("every export of an app carries the values in force of every key its run read, shared or not … from
 * the run's provenance and never from today's registry") and ADR 9000 (the units-sent cap in force, its
 * market value and store overrides, on every Compacting workbook).
 *
 * **Where the values come from.** Only from what the run itself recorded (bata-platform ADR 9045): the
 * in-season run's `GET /inseason/runs/{run_id}/provenance` (`parameters_in_force`), or the consolidation
 * run's `GET /exit/consolidation/runs/{run_id}/parameters`. Nothing here reads the registry: a run that
 * recorded nothing, a platform that does not publish the record yet, a read that failed, a file that is
 * not one run's — each gets a sheet that SAYS so in one plain sentence, never values re-read today.
 *
 * **Workbook only** (`docs/adr/9041`, `docs/adr/9045`, `docs/adr/9072`): the CSV is the request's one
 * table, byte for byte, and gains nothing. The sheet goes after the file's own sheets, so the table a
 * planner opens the file for stays first.
 *
 * **Labels** are the register's own (`parameterName`, `PARAMETER_TEXTS`), in the workbook's language; a
 * key with no drafted label is shown as its key. The key is also its own column, for a search.
 */

import { callOperation } from '@/api';
import { runProvenance } from '@/api/generated';
import {
  CONSOLIDATION_RUN_PARAMETERS_READ,
  provenanceParametersOf,
  type ConsolidationRunParametersRead,
  type ParameterInForceShape,
  type ParametersInForceShape,
} from '@/api/pending/run-parameters';
import type { ExportCell, ExportColumn, ExportRequest, WorkbookSheet } from '@/export';
import { parameterName } from './parameter-texts';

/** Why a file states no parameters. Each has its own sentence (`ABSENCE_KEY`). */
export const PARAMETERS_ABSENCES = ['no_run', 'several_runs', 'not_recorded_by_run', 'not_published', 'unread'] as const;
export type ParametersAbsence = (typeof PARAMETERS_ABSENCES)[number];

/** What a file can say of its run's parameters: the record, or why there is none. */
export type ParametersInForce =
  | { readonly state: 'recorded'; readonly runId: string; readonly record: ParametersInForceShape }
  | { readonly state: 'absent'; readonly runId: string | null; readonly reason: ParametersAbsence };

export const PARAMETERS_SHEET_TITLE_KEY = 'planning.parametersInForce.sheetTitle';

const ABSENCE_KEY: Readonly<Record<ParametersAbsence, string>> = {
  no_run: 'planning.parametersInForce.absent.noRun',
  several_runs: 'planning.parametersInForce.absent.severalRuns',
  not_recorded_by_run: 'planning.parametersInForce.absent.notRecordedByRun',
  not_published: 'planning.parametersInForce.absent.notPublished',
  unread: 'planning.parametersInForce.absent.unread',
};

/** A row's state: a value was in force, or the run read the key and found none. */
const IN_FORCE = 'in_force';
const NOT_IN_FORCE = 'not_in_force';
const STATE_KEY: Readonly<Record<string, string>> = {
  [IN_FORCE]: 'planning.parametersInForce.state.inForce',
  [NOT_IN_FORCE]: 'planning.parametersInForce.state.notInForce',
};
/** The scope code of the estate row (both scope columns empty). */
const ESTATE = 'estate';

/** The recorded sheet's columns. Keys spelled out one by one so the catalogue check sees every key. */
export const PARAMETERS_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'parameter', headerKey: 'planning.parametersInForce.column.parameter', kind: 'label', freeze: true,
    descriptionKey: 'planning.parametersInForce.columnGuide.parameter',
    display: (code, t) => parameterName((key, values) => t(key, values), code),
  },
  {
    key: 'parameter_key', headerKey: 'planning.parametersInForce.column.parameterKey', kind: 'text',
    descriptionKey: 'planning.parametersInForce.columnGuide.parameterKey',
  },
  {
    key: 'scope', headerKey: 'planning.parametersInForce.column.scope', kind: 'label',
    descriptionKey: 'planning.parametersInForce.columnGuide.scope',
    display: (code, t) => (code === ESTATE ? t('planning.parametersInForce.scope.estate') : code),
  },
  {
    key: 'numeric_value', headerKey: 'planning.parametersInForce.column.numericValue', kind: 'number', decimals: 6,
    descriptionKey: 'planning.parametersInForce.columnGuide.numericValue',
  },
  {
    key: 'text_value', headerKey: 'planning.parametersInForce.column.textValue', kind: 'text',
    descriptionKey: 'planning.parametersInForce.columnGuide.textValue',
  },
  {
    key: 'state', headerKey: 'planning.parametersInForce.column.state', kind: 'label',
    descriptionKey: 'planning.parametersInForce.columnGuide.state',
    display: (code, t) => t(STATE_KEY[code] ?? code),
  },
  {
    key: 'valid_from', headerKey: 'planning.parametersInForce.column.validFrom', kind: 'date',
    descriptionKey: 'planning.parametersInForce.columnGuide.validFrom',
  },
  {
    key: 'valid_to', headerKey: 'planning.parametersInForce.column.validTo', kind: 'date',
    descriptionKey: 'planning.parametersInForce.columnGuide.validTo',
  },
  {
    key: 'read_on', headerKey: 'planning.parametersInForce.column.readOn', kind: 'date',
    descriptionKey: 'planning.parametersInForce.columnGuide.readOn',
  },
  {
    key: 'change_set_id', headerKey: 'planning.parametersInForce.column.changeSetId', kind: 'text',
    descriptionKey: 'planning.parametersInForce.columnGuide.changeSetId',
  },
];

/** The sheet's columns when the file states no parameters: the sentence, and the run it is about. */
export const PARAMETERS_ABSENT_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'note', headerKey: 'planning.parametersInForce.column.note', kind: 'label',
    descriptionKey: 'planning.parametersInForce.columnGuide.note',
    display: (code, t) => t(ABSENCE_KEY[code as ParametersAbsence] ?? code),
  },
  {
    key: 'run_id', headerKey: 'planning.parametersInForce.column.runId', kind: 'text',
    descriptionKey: 'planning.parametersInForce.columnGuide.runId',
  },
];

const MISSING: ExportCell = { kind: 'missing', state: 'not-reported' };

function textOr(value: string | null): ExportCell {
  return value === null || value === '' ? MISSING : { kind: 'text', value };
}

function dateOr(value: string | null): ExportCell {
  return value === null || value === '' ? MISSING : { kind: 'date', iso: value };
}

/** The scope as written: `estate`, or the row's `dimension=value` pairs (ADR 9007's `;`-joined form). */
export function scopeCode(row: Pick<ParameterInForceShape, 'scope_dimension' | 'scope_value'>): string {
  if (row.scope_dimension === '' && row.scope_value === '') return ESTATE;
  const dimensions = row.scope_dimension.split(';');
  const values = row.scope_value.split(';');
  return dimensions.map((dimension, index) => `${dimension}=${values[index] ?? ''}`).join(';');
}

function parameterRow(row: ParameterInForceShape): readonly ExportCell[] {
  const absent = row.absent_reason !== null && row.absent_reason !== '';
  return [
    { kind: 'label', value: row.key, code: row.key },
    { kind: 'text', value: row.key },
    { kind: 'label', value: scopeCode(row), code: scopeCode(row) },
    row.numeric_value === null ? MISSING : { kind: 'number', raw: row.numeric_value },
    textOr(row.text_value),
    { kind: 'label', value: absent ? NOT_IN_FORCE : IN_FORCE, code: absent ? NOT_IN_FORCE : IN_FORCE },
    dateOr(row.valid_from),
    dateOr(row.valid_to),
    dateOr(row.read_on),
    textOr(row.change_set_id),
  ];
}

/** The sheet for what a file can say of its run's parameters. Pure. */
export function parametersInForceSheet(found: ParametersInForce): WorkbookSheet {
  if (found.state === 'recorded') {
    const rows = found.record.parameters.map(parameterRow);
    return { titleKey: PARAMETERS_SHEET_TITLE_KEY, columns: PARAMETERS_COLUMNS, rows: () => rows };
  }
  const rows: readonly (readonly ExportCell[])[] = [[
    { kind: 'label', value: found.reason, code: found.reason },
    textOr(found.runId),
  ]];
  return { titleKey: PARAMETERS_SHEET_TITLE_KEY, columns: PARAMETERS_ABSENT_COLUMNS, rows: () => rows };
}

/**
 * `request` with the «Parametri in vigore» sheet after its own sheets. The request's table is untouched
 * (it is the CSV); a request with no workbook gets its table as the first sheet, as `withDroppedSheet` does.
 */
export function withParametersInForce(request: ExportRequest, found: ParametersInForce): ExportRequest {
  const own: readonly WorkbookSheet[] = request.workbook?.sheets
    ?? [{ titleKey: request.titleKey, columns: request.columns, rows: request.rows }];
  return {
    ...request,
    workbook: {
      ...(request.workbook?.titleKey === undefined ? {} : { titleKey: request.workbook.titleKey }),
      sheets: [...own, parametersInForceSheet(found)],
    },
  };
}

/** A file whose rows are not one run's output (a dated read, a season's positions). */
export const NO_RUN: ParametersInForce = { state: 'absent', runId: null, reason: 'no_run' };

function fromRecord(runId: string, record: ParametersInForceShape | null | undefined): ParametersInForce {
  if (record === undefined || record === null) return { state: 'absent', runId, reason: 'not_published' };
  if (!record.recorded) return { state: 'absent', runId, reason: 'not_recorded_by_run' };
  return { state: 'recorded', runId, record };
}

/** The in-season run's record, from its provenance read. Never rejects: a failed read is `unread`. */
export async function readInSeasonRunParameters(runId: string | null): Promise<ParametersInForce> {
  if (runId === null) return NO_RUN;
  try {
    return fromRecord(runId, provenanceParametersOf(await callOperation(runProvenance, { path: { run_id: runId } })));
  } catch {
    return { state: 'absent', runId, reason: 'unread' };
  }
}

/**
 * One consolidation run's record — position, decision or lane-move run alike. Never rejects. `read` is
 * the pinned operation; `null` states `not_published` and issues no request.
 */
export async function readConsolidationRunParameters(
  runId: string | null, read: ConsolidationRunParametersRead | null = CONSOLIDATION_RUN_PARAMETERS_READ,
): Promise<ParametersInForce> {
  if (runId === null) return NO_RUN;
  if (read === null) return { state: 'absent', runId, reason: 'not_published' };
  try {
    return fromRecord(runId, await callOperation(read, { path: { run_id: runId } }));
  } catch {
    return { state: 'absent', runId, reason: 'unread' };
  }
}

/** The one run a file's rows come from, or why there is none: none, or more than one. */
export function oneRun(runIds: Iterable<string>): string | ParametersInForce {
  const distinct = [...new Set(runIds)];
  if (distinct.length === 0) return NO_RUN;
  if (distinct.length > 1) return { state: 'absent', runId: null, reason: 'several_runs' };
  return distinct[0] as string;
}
